// Kundenansicht-Vorschau: Admin/Mitarbeiter sehen das echte Portal als Kunde (schreibgeschützt).
// Zwei Betriebsarten:
//  • Eigener Tab: Vorschau-Token liegt in sessionStorage (ka_token), die Admin-Sitzung in localStorage bleibt unberührt.
//  • Eingebettet (iframe mit name="kundenansicht-frame", z. B. Kundenansicht-Vorschau in der Objektakte):
//    Token/Status leben in window.name des Frames (überlebt Reloads, berührt weder localStorage noch die
//    mit dem Eltern-Tab geteilte sessionStorage).
// Muss vor allen anderen Modulen laden.
import { toast } from 'sonner';

const FRAME_PREFIX = 'kundenansicht-frame';
const KA_KEYS = ['kundenansicht', 'ka_token', 'ka_user', 'ka_info', 'ka_back'];
const LS_MAP = { token: 'ka_token', user: 'ka_user' };
export const BLOCK_MSG = 'Vorschau-Modus: In der Kundenansicht wird nichts gesendet oder gespeichert.';

const isFrame = (() => {
  try { return window.self !== window.top && String(window.name || '').startsWith(FRAME_PREFIX); } catch { return false; }
})();

// Frame-Store (persistiert in window.name)
const frameStore = {
  read() {
    try {
      const raw = String(window.name || '');
      const json = raw.slice(FRAME_PREFIX.length).replace(/^:/, '');
      return json ? JSON.parse(json) : {};
    } catch { return {}; }
  },
  write(obj) { try { window.name = `${FRAME_PREFIX}:${JSON.stringify(obj)}`; } catch { /* noop */ } },
  get(k) { const v = this.read()[k]; return v === undefined ? null : v; },
  set(k, v) { const o = this.read(); o[k] = String(v); this.write(o); },
  remove(k) { const o = this.read(); delete o[k]; this.write(o); },
};

(function installKundenansichtStorage() {
  try {
    const proto = Storage.prototype;
    const og = proto.getItem, os = proto.setItem, orm = proto.removeItem;
    const active = () => og.call(window.sessionStorage, 'kundenansicht') === '1';
    proto.getItem = function (k) {
      if (isFrame) {
        if (this === window.sessionStorage && KA_KEYS.includes(k)) return frameStore.get(k);
        if (this === window.localStorage && LS_MAP[k]) return frameStore.get(LS_MAP[k]);
        return og.call(this, k);
      }
      if (this === window.localStorage && LS_MAP[k] && active()) return og.call(window.sessionStorage, LS_MAP[k]);
      return og.call(this, k);
    };
    proto.setItem = function (k, v) {
      if (isFrame) {
        if (this === window.sessionStorage && KA_KEYS.includes(k)) return frameStore.set(k, v);
        if (this === window.localStorage && LS_MAP[k]) return frameStore.set(LS_MAP[k], v);
        return os.call(this, k, v);
      }
      if (this === window.localStorage && LS_MAP[k] && active()) return os.call(window.sessionStorage, LS_MAP[k], v);
      return os.call(this, k, v);
    };
    proto.removeItem = function (k) {
      if (isFrame) {
        if (this === window.sessionStorage && KA_KEYS.includes(k)) return frameStore.remove(k);
        if (this === window.localStorage && LS_MAP[k]) return frameStore.remove(LS_MAP[k]);
        return orm.call(this, k);
      }
      if (this === window.localStorage && LS_MAP[k] && active()) return orm.call(window.sessionStorage, LS_MAP[k]);
      return orm.call(this, k);
    };
  } catch (e) { /* Storage nicht verfügbar */ }
})();

export const isKundenansichtFrame = () => isFrame;

export const isKundenansicht = () => {
  try { return sessionStorage.getItem('kundenansicht') === '1'; } catch { return false; }
};

export const kundenansichtInfo = () => {
  try { return JSON.parse(sessionStorage.getItem('ka_info') || 'null'); } catch { return null; }
};

export const endKundenansicht = () => {
  let back = null;
  try { back = sessionStorage.getItem('ka_back'); } catch { /* noop */ }
  try { KA_KEYS.forEach(k => sessionStorage.removeItem(k)); } catch { /* noop */ }
  if (isFrame) {
    try { window.parent.postMessage({ type: 'kundenansicht-end' }, window.location.origin); } catch { /* noop */ }
    return;
  }
  // Im gleichen Fenster geöffnet → zurück in die Kundenakte (Admin-Sitzung liegt unverändert in localStorage)
  if (back) { window.location.replace(back); return; }
  window.close();
  setTimeout(() => { window.location.replace('/kundenansicht/beendet'); }, 150);
};

// Schreibschutz im Frontend: In der Kundenansicht verlässt keine Änderung den Browser.
// (Der Server blockt Vorschau-Tokens zusätzlich – hier gibt es sofort den Hinweis, ohne Netzwerk-Roundtrip.)
(function installReadOnlyFetch() {
  try {
    const origFetch = window.fetch.bind(window);
    window.fetch = function (input, init) {
      if (isKundenansicht()) {
        const method = String((init && init.method) || (input && input.method) || 'GET').toUpperCase();
        const url = typeof input === 'string' ? input : String((input && input.url) || '');
        const mutating = !['GET', 'HEAD', 'OPTIONS'].includes(method);
        if (mutating && url.includes('/api/') && !url.includes('/api/kundenansicht/exchange')) {
          toast.info('Vorschau-Modus', { id: 'ka-blocked', description: 'In der Kundenansicht wird nichts gesendet oder gespeichert – so sieht der Kunde das Portal.' });
          return Promise.resolve(new Response(JSON.stringify({ detail: BLOCK_MSG }), { status: 403, headers: { 'Content-Type': 'application/json' } }));
        }
      }
      return origFetch(input, init);
    };
  } catch (e) { /* noop */ }
})();
